import { useCachedDocumentFile } from "@/features/documents";
import { PdfViewer } from "./PdfViewer";
import { ImageViewer } from "./ImageViewer";
import { Loader2, Download } from "lucide-react";
import IconButton from "@/components/ui/icon-button";

interface CachedFileRendererProps {
    url: string;
    fileKey: string;
    isPdf: boolean;
}

export function CachedFileRenderer({
    url,
    fileKey,
    isPdf,
}: CachedFileRendererProps) {
    const {
        data: cachedUrl,
        isLoading,
        error,
    } = useCachedDocumentFile(url, fileKey);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-2">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-xs font-semibold">Loading file...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-red-500">
                Error loading document data:{" "}
                {error instanceof Error ? error.message : "Unknown error"}
            </div>
        );
    }

    if (!cachedUrl) return <div>No document data available</div>;

    const handleDownload = () => {
        const link = document.createElement("a");
        link.href = cachedUrl;
        link.download = fileKey;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="relative w-full h-full">
            {isPdf ? (
                <PdfViewer file={cachedUrl} alt="Document" />
            ) : (
                <ImageViewer image={cachedUrl} alt="Document" />
            )}
            <IconButton
                onClick={handleDownload}
                className="absolute bottom-2 right-2 z-10"
                size="sm"
                color="tertiary"
            >
                <Download className="h-4 w-4 text-md-on-surface" />
            </IconButton>
        </div>
    );
}
